import { Fragment, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Dialog, Disclosure, Transition } from "@headlessui/react";
import ProgressCircle from "../components/ProgressCircle";

import { ReactComponent as BackArrow } from "../assets/icons/BackArrow.svg";
import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";
import { ReactComponent as Info } from "../assets/icons/Info.svg";
import { ReactComponent as Caret } from "../assets/icons/Caret.svg";
import { ReactComponent as HbA1cOptimal } from "../assets/icons/HbA1cOptimal.svg";
import { ReactComponent as HbA1cNonOptimal } from "../assets/icons/HbA1cNonOptimal.svg";
import { ReactComponent as Download } from "../assets/icons/Download.svg";

import { constants } from "../utils/ranges";
import CompanyLogo from "../assets/images/CareplixLogo.png";
import VendorLogo from "../assets/images/CiplaLogo.png";

import {
  Font,
  Document,
  Page,
  StyleSheet,
  View,
  Text,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import { useAuth } from "../utils/auth";

Font.register({
  family: "Poppins",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLDz8V1tvFP-KUEg.ttf",
      fontWeight: "light",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrFJDUc1NECPY.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9V1tvFP-KUEg.ttf",
      fontWeight: "medium",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLEj6V1tvFP-KUEg.ttf",
      fontWeight: "semibold",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLCz7V1tvFP-KUEg.ttf",
      fontWeight: "bold",
    },
  ],
});

const PDFReport = ({
  referralCode = "",
  name = "",
  age = 0,
  gender = "",
  height = 0,
  weight = 0,
  result = {
    timestamp: new Date(),
    heart_rate: 0,
    bmi: 0,
    HbA1c: 0.0,
    diabetes_control_score: 0,
    blood_volume: 0.0,
    tbw: 0.0,
    tbwp: 0,
    bodyfat: 0,
    weight: 0,
  },
}) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#fff",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "space-between",
    },
    header: {
      backgroundColor: "#f7f7f7",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 48,
    },
    headerIcon: { flex: 0.3 },
    contentContainer: { flex: 1, paddingVertical: 32, paddingHorizontal: 64 },
    rowContainer: { flexDirection: "row", justifyContent: "space-between" },
    largeText: { fontFamily: "Poppins", fontSize: 15, fontWeight: "bold" },
    normalText: { fontFamily: "Poppins", fontSize: 12, fontWeight: "semibold" },
    smallText: { fontFamily: "Poppins", fontSize: 10 },
    footer: {
      height: 48,
      backgroundColor: "#454E92",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    footerText: {
      fontFamily: "Poppins",
      fontWeight: "light",
      fontSize: 8,
      textAlign: "center",
      color: "#fff",
    },
  });

  return (
    <Document
      title="CarePlix Report"
      subject="Report generated from CarePlix Vitals Scan"
      author="CarePlix"
    >
      <Page style={styles.page}>
        <View fixed style={styles.header}>
          <Image style={styles.headerIcon} source={CompanyLogo} />
          <Image style={styles.headerIcon} source={VendorLogo} />
        </View>
        <View style={styles.contentContainer}>
          <Text style={[styles.largeText, { textTransform: "capitalize" }]}>
            {name}
          </Text>
          <View style={styles.rowContainer}>
            <View>
              {[
                { label: "Gender", value: gender },
                { label: "Age", value: `${age} years` },
                { label: "Height", value: `${height} cms` },
                { label: "Weight", value: `${weight} kgs` },
              ].map(({ label, value }) => (
                <View
                  key={label.toLowerCase().replace(/\s+/g, "-")}
                  style={{ display: "flex", flexDirection: "row" }}
                >
                  <Text style={styles.normalText}>{label}: </Text>
                  <Text style={[styles.normalText, { fontWeight: "normal" }]}>
                    {value}
                  </Text>
                </View>
              ))}
            </View>
            <View>
              {[
                {
                  label: "Assessment Date",
                  value: result.timestamp.toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }),
                },
                {
                  label: "Assessment Time",
                  value: result.timestamp.toLocaleTimeString(undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  }),
                },
                { label: "Referral Code", value: referralCode },
              ].map(({ label, value }) => (
                <View
                  key={label.toLowerCase().replace(/\s+/g, "-")}
                  style={{ display: "flex", flexDirection: "row" }}
                >
                  <Text style={styles.normalText}>{label}: </Text>
                  <Text style={[styles.normalText, { fontWeight: "normal" }]}>
                    {value}
                  </Text>
                </View>
              ))}
            </View>
          </View>
          <Text
            style={[
              styles.largeText,
              {
                alignSelf: "center",
                marginVertical: 36,
                paddingVertical: 8,
                paddingHorizontal: 32,
                borderRadius: 10,
                backgroundColor: "#454E92",
                color: "#fff",
                fontWeight: "medium",
              },
            ]}
          >
            Digital Health Assessment Report
          </Text>
          <View style={styles.rowContainer}>
            <View style={{ flex: 4 }}>
              <Text style={styles.normalText}>Blood Biomarker</Text>
              {["HbA1c Level", "Diabetes Control Score"].map((e, idx) => (
                <Text
                  key={`entry-${idx}`}
                  style={[styles.smallText, { marginTop: 10 }]}
                >
                  {e}
                </Text>
              ))}
            </View>
            <View style={{ flex: 3 }}>
              <Text style={styles.normalText}>Value</Text>
              {[`${result.HbA1c}%`, `${result.diabetes_control_score}%`].map(
                (e, idx) => (
                  <Text
                    key={`entry-${idx}`}
                    style={[styles.smallText, { marginTop: 10 }]}
                  >
                    {e}
                  </Text>
                )
              )}
            </View>
            <View style={{ flex: 3 }}>
              <Text style={styles.normalText}>Range</Text>
              {["<5.7%", "75% - 100%"].map((e, idx) => (
                <Text
                  key={`entry-${idx}`}
                  style={[styles.smallText, { marginTop: 10 }]}
                >
                  {e}
                </Text>
              ))}
            </View>
          </View>
          <View style={[styles.rowContainer, { marginTop: 36 }]}>
            <View style={{ flex: 4 }}>
              <Text style={styles.normalText}>Heart Health</Text>
              {["Heart Rate"].map((e, idx) => (
                <Text
                  key={`entry-${idx}`}
                  style={[styles.smallText, { marginTop: 10 }]}
                >
                  {e}
                </Text>
              ))}
            </View>
            <View style={{ flex: 3 }}>
              <Text style={styles.normalText}>Value</Text>
              {[`${result.heart_rate} bpm`].map((e, idx) => (
                <Text
                  key={`entry-${idx}`}
                  style={[styles.smallText, { marginTop: 10 }]}
                >
                  {e}
                </Text>
              ))}
            </View>
            <View style={{ flex: 3 }}>
              <Text style={styles.normalText}>Range</Text>
              {["60-100 bpm"].map((e, idx) => (
                <Text
                  key={`entry-${idx}`}
                  style={[styles.smallText, { marginTop: 10 }]}
                >
                  {e}
                </Text>
              ))}
            </View>
          </View>
          <View
            style={{
              marginTop: 120,
              padding: 24,
              borderRadius: 10,
              backgroundColor: "#f1f1f1",
            }}
          >
            <Text style={[styles.normalText, { fontWeight: "bold" }]}>
              DISCLAIMER:
            </Text>
            <Text
              style={[styles.smallText, { marginTop: 8, textAlign: "justify" }]}
            >
              For Investigational Use Only. Careplix is not a substitute for the
              clinical judgment of a health care professional. Careplix is
              intended to improve your awareness of general wellness. Careplix
              does not diagnose, treat, mitigate or prevent any disease,
              symptom, disorder or abnormal physical state. Consult with a
              health care professional or emergency services if you believe you
              may have a medical issue.
            </Text>
          </View>
        </View>
        <View style={styles.contentContainer} break>
          <View style={styles.rowContainer}>
            <View style={{ flex: 4 }}>
              <Text style={styles.normalText}>Physiological</Text>
              <Text
                style={{
                  marginTop: 4,
                  width: "75%",
                  fontFamily: "Poppins",
                  fontSize: 9,
                  fontWeight: "light",
                }}
              >
                *Calculated based on height and weight given by you.
              </Text>
              <View>
                {[
                  "BMI",
                  "Blood Volume",
                  "Total Body Water",
                  "Body Water %",
                  "Total Body Fat",
                  "Body Weight",
                ].map((e, idx) => (
                  <Text
                    key={`entry-${idx}`}
                    style={[styles.smallText, { marginTop: 15 }]}
                  >
                    {e}
                  </Text>
                ))}
              </View>
            </View>
            <View style={{ flex: 3, justifyContent: "space-between" }}>
              <Text style={styles.normalText}>Value</Text>
              <View>
                {[
                  `${result.bmi} kg/m²`,
                  `${result.blood_volume} ml`,
                  `${result.tbw} liters`,
                  `${result.tbwp}%`,
                  `${result.bodyfat}%`,
                  `${result.weight} kg`,
                ].map((e, idx) => (
                  <Text
                    key={`entry-${idx}`}
                    style={[styles.smallText, { marginTop: 15 }]}
                  >
                    {e}
                  </Text>
                ))}
              </View>
            </View>
            <View style={{ flex: 3, justifyContent: "space-between" }}>
              <Text style={styles.normalText}>Range</Text>
              <View>
                {["18.5-24.9 kg/m²", "--", "--", "--", "--", "--"].map(
                  (e, idx) => (
                    <Text
                      key={`entry-${idx}`}
                      style={[styles.smallText, { marginTop: 15 }]}
                    >
                      {e}
                    </Text>
                  )
                )}
              </View>
            </View>
          </View>
          <View
            style={{
              marginTop: 120,
              padding: 24,
              borderRadius: 10,
              backgroundColor: "#f1f1f1",
            }}
          >
            <Text style={[styles.normalText, { fontWeight: "bold" }]}>
              DISCLAIMER:
            </Text>
            <Text
              style={[styles.smallText, { marginTop: 8, textAlign: "justify" }]}
            >
              For Investigational Use Only. Careplix is not a substitute for the
              clinical judgment of a health care professional. Careplix is
              intended to improve your awareness of general wellness. Careplix
              does not diagnose, treat, mitigate or prevent any disease,
              symptom, disorder or abnormal physical state. Consult with a
              health care professional or emergency services if you believe you
              may have a medical issue.
            </Text>
          </View>
        </View>
        <View style={styles.contentContainer} break>
          {[
            {
              title: "HbA1c Level",
              desc: "HbA1c is measured primarily to determine the three-month average blood sugar level and is used as a standard diagnostic test for evaluating the risk of complications of diabetes and as an assessment of glycemic control.",
            },
            {
              title: "Diabetes Control",
              desc: "The Diabetes Control Score (DCS) is a numerical value used to assess how well an individual is managing their condition and maintaining optimal blood glucose levels. It provides insight into the effectiveness of diabetes management strategies.",
            },
            {
              title: "Heart Rate",
              desc: "The heart rate is the number of times heart beats in a minute. A normal resting heart rate for adults ranges from 60 to 100 beats per minute.",
            },
            {
              title: "BMI",
              desc: "BMI is the measure of body fat based on height and weight. The normal range of BMI is 18.5 - 24.9 kg/m2.",
            },
            {
              title: "Blood Volume",
              desc: "Blood volume refers to the total amount of fluid circulating within the arteries, capillaries, veins, venules, and chambers of the heart at any time.",
            },
            {
              title: "Total Body Water & Body water Percentage",
              desc: "Total body water is the amount of water present in the human body. This includes fluids both inside and outside of cells. An adult body water percentage should fall between 40% and 60%.",
            },
            {
              title: "Total Body Fat",
              desc: "Body fat percentage is basically the proportion of the mass of fat in our body relative to the total mass of our body.",
            },
            {
              title: "Body Weight",
              desc: "Body weight refers to the total mass of a person's body, which is composed of various tissues, such as bones, muscles, organs, and body fat.",
            },
          ].map(({ title, desc }) => (
            <View
              key={`info-${title.toLowerCase().replace(/\s+/g, "-")}`}
              style={{ marginBottom: 16 }}
              wrap={false}
            >
              <Text style={[styles.largeText, { fontWeight: "semibold" }]}>
                {title}
              </Text>
              <Text
                style={[
                  styles.normalText,
                  { fontWeight: "light", textAlign: "justify", marginTop: 6 },
                ]}
              >
                {desc}
              </Text>
            </View>
          ))}
        </View>
        <View fixed style={styles.footer}>
          <Text style={styles.footerText}>
            Report Generated from&nbsp;
            {window.location.hostname}
            &nbsp;on&nbsp;
            {new Date().toISOString()}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const Range = ({
  ranges = [
    { point: 0, label: "Start", color: "#ccc" },
    { point: 100, label: "End", color: "#000" },
  ],
  selectedIndex,
  className = "",
}) => {
  selectedIndex -= ranges.length - 1 === selectedIndex;
  return (
    <div className={`relative grid grid-flow-col auto-cols-fr ${className}`}>
      {ranges.slice(0, -1).map((rangeItem, index, list) => (
        <div key={`range-${index}`} className="flex flex-col justify-end">
          {index === selectedIndex && (
            <Caret
              className="self-center flex-shrink-0 w-2 h-1"
              style={{ color: ranges[selectedIndex].color }}
            />
          )}
          <h6
            className="text-primary/60 text-center text-xxs font-medium uppercase truncate"
            style={
              index === selectedIndex
                ? { color: ranges[selectedIndex].color }
                : {}
            }
          >
            {rangeItem.label}
          </h6>
          <div
            className="my-1 h-1"
            style={{
              backgroundColor:
                index === selectedIndex ? ranges[selectedIndex].color : "#ccc",
              borderTopLeftRadius: index === 0 ? 9999 : undefined,
              borderBottomLeftRadius: index === 0 ? 9999 : undefined,
              borderTopRightRadius:
                index === list.length - 1 ? 9999 : undefined,
              borderBottomRightRadius:
                index === list.length - 1 ? 9999 : undefined,
            }}
          />
          <p className="text-primary/70 text-xxs">{rangeItem.point}</p>
        </div>
      ))}
      <p className="absolute bottom-0 right-0 text-primary/70 text-xxs">
        {ranges[ranges.length - 1].point}
      </p>
    </div>
  );
};

const ListItemContainer = ({
  disabled = false,
  label = "",
  defaultExpanded = false,
  title,
  desc,
  className = "",
  ...props
}) => {
  return (
    <Disclosure
      defaultOpen={defaultExpanded}
      as="div"
      className="px-6 py-4 rounded-2xl border border-primary bg-white shadow-box"
    >
      <Disclosure.Button as="div" disabled={disabled}>
        {({ open }) => [
          <div
            key="list-item-container-head"
            className="flex items-start justify-between space-x-1"
          >
            <h2 className="text-primary text-xs font-medium">{label}</h2>
            <Chevron
              className={`h-3 w-3 text-primary/60 transition-transform duration-300 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          </div>,
          <Fragment key="list-item-container-body">{props.children}</Fragment>,
        ]}
      </Disclosure.Button>
      <Disclosure.Panel>
        {typeof title === "string" && (
          <h5 className="mt-2.5 text-primary text-xs font-medium">{title}</h5>
        )}
        {typeof desc === "string" && (
          <p className="mt-1.5 text-primary/70 text-xxs whitespace-pre-line">
            {desc}
          </p>
        )}
      </Disclosure.Panel>
    </Disclosure>
  );
};

const ScanResult = () => {
  const location = useLocation();
  const result = location.state?.result;
  const {
    userData: { fname, lname, dob, gender, height, weight, referral_details },
  } = useAuth();

  const reportPDF = useMemo(() => {
    const name = `${fname} ${lname}`;
    const ageDifMs = Date.now() - new Date(dob).getTime();
    const ageDate = new Date(ageDifMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    const gen = `${gender.slice(0, 1).toUpperCase()}${gender.slice(1)}`;
    const vitalsData = {
      timestamp: new Date(result?.entry_time?.replace?.(" ", "T") ?? 0),
      heart_rate: result?.vitals.heart_rate,
      bmi: result?.metadata.physiological_scores.bmi,
      HbA1c: result?.metadata?.glucose_info.hba1c < 5.7 ? "<5.7" : "≥5.7",
      diabetes_control_score:
        result?.metadata?.glucose_info.diabetes_control_score,
      blood_volume: result?.metadata?.physiological_scores.bloodvolume,
      tbw: result?.metadata?.physiological_scores.tbw,
      tbwp: result?.metadata?.physiological_scores.tbwp,
      bodyfat: result?.metadata?.physiological_scores.bodyfat,
      weight: result?.metadata?.physiological_scores.weight,
    };
    return (
      <PDFReport
        referralCode={referral_details?.[0]?.code ?? ""}
        name={name}
        age={age}
        gender={gen}
        height={height}
        weight={weight}
        result={vitalsData}
      />
    );
  }, [referral_details, fname, lname, dob, gender, height, weight, result]);
  const [modalState, setModalState] = useState({
    visible: false,
    title: "",
    desc: "",
  });
  const closeModal = () =>
    setModalState({ visible: false, title: "", desc: "" });

  return result?.scan_id?.length > 0 ? (
    <section className="px-6 py-2">
      <div className="flex items-center text-primary">
        <Link className="flex-shrink-0 p-1 pr-2.5" to="/" replace>
          <BackArrow className="h-2.5 w-2.5" />
        </Link>
        <h3 className="text-sm font-medium">Result</h3>
      </div>
      <div className="mt-4 mb-6 space-y-6">
        {(() => {
          const score = result?.metadata?.glucose_info?.diabetes_control_score;
          const selectedScoreRange = constants[
            "Diabetes Control Score"
          ].ranges.reduce((p, c) => (score > c.point ? c : p));
          return (
            <>
              <div className="flex flex-col items-center">
                <ProgressCircle
                  size={120}
                  value={score}
                  color={selectedScoreRange.color}
                />
                <h2 className="mt-3 text-sm font-medium">
                  Diabetes Control Score
                </h2>
                <h4
                  className="mt-1 text-sm font-medium uppercase"
                  style={{ color: selectedScoreRange.color }}
                >
                  {selectedScoreRange.label}
                </h4>
              </div>
              <p className="!mt-4 text-xxs font-light leading-tight text-justify whitespace-pre-line">
                {constants["Diabetes Control Score"].info}
              </p>
            </>
          );
        })()}
        {(() => {
          if (typeof result?.metadata?.glucose_info?.hba1c !== "number")
            return null;
          const hba1c = result?.metadata.glucose_info.hba1c;
          return (
            <div className="px-6 py-4 rounded-2xl border border-secondary bg-primary text-white shadow-box">
              <div className="flex items-center justify-between space-x-4 text-white">
                {hba1c < 5.7 ? (
                  <HbA1cOptimal height={36} width={36} />
                ) : (
                  <HbA1cNonOptimal height={36} width={36} />
                )}
                <div className="grow">
                  <h2 className="text-xs font-medium text-white">
                    Predicted HbA1c Level
                  </h2>
                  <h4 className="mt-1 text-xxs font-medium opacity-70">
                    {hba1c < 5.7 ? "Optimal" : "Above Optimal"}
                  </h4>
                </div>
                <div className="shrink-0 text-right font-semibold">
                  <h4 className="text-xl">{hba1c < 5.7 ? "<5.7" : "≥5.7"}</h4>
                  <h5 className="opacity-70 text-xxs">
                    {constants["HbA1c"].unit}
                  </h5>
                </div>
              </div>
              <div className="relative my-1 py-4 mx-8 grid grid-flow-col auto-cols-fr">
                <div style={{ color: constants["HbA1c"].ranges[0].color }}>
                  <h6 className="opacity-60 text-left text-xxs font-medium uppercase truncate">
                    {constants["HbA1c"].ranges[0].label}
                  </h6>
                  <div className="my-1 h-1 bg-current rounded-l-full" />
                </div>
                <div style={{ color: constants["HbA1c"].ranges[1].color }}>
                  <h6 className="opacity-60 text-center text-xxs font-medium uppercase truncate">
                    {constants["HbA1c"].ranges[1].label}
                  </h6>
                  <div className="my-1 h-1 bg-current rounded-none" />
                  <p className="ml-1 !text-white/70 text-xs">
                    {constants["HbA1c"].ranges[1].point}
                  </p>
                </div>
                <div style={{ color: constants["HbA1c"].ranges[2].color }}>
                  <h6 className="opacity-60 text-right text-xxs font-medium uppercase truncate">
                    {constants["HbA1c"].ranges[2].label}
                  </h6>
                  <div className="my-1 h-1 bg-current rounded-r-full" />
                  <p className="-ml-2 !text-white/70 text-xs">
                    {constants["HbA1c"].ranges[2].point}
                  </p>
                </div>
                <div className="absolute left-1/3 top-0 bottom-0 border-l-2 border-dashed border-white/60" />
                <svg
                  className={`absolute left-1/3 top-0.5 h-2.5 ${
                    hba1c < 5.7
                      ? "-translate-x-[250%] rotate-180"
                      : "translate-x-[150%]"
                  }`}
                  viewBox="0 0 6 11"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.5 0.5L0.5 10.5L5.5 5.42754L0.5 0.5Z"
                    fill="#fff"
                  />
                </svg>
              </div>
              <p className="text-xxs text-white/60">
                We have estimated your HbA1c level to be around&nbsp;
                <span className="text-white">{hba1c}%</span>.<br />
                Please note that this is an approximate value and may vary.
              </p>
            </div>
          );
        })()}
        {(() => {
          const bmi = result?.metadata.physiological_scores.bmi;
          const selectedBMIRangeIndex = constants.BMI.ranges.reduce(
            (p, c, ci) => (bmi > c.point ? ci : p),
            0
          );
          return (
            <ListItemContainer
              label="BMI"
              title={`Your BMI is ${constants.BMI.ranges[selectedBMIRangeIndex].label}`}
              desc={
                constants.BMI.info +
                "\n\n" +
                constants.BMI.ranges[selectedBMIRangeIndex].desc
              }
            >
              <div className="py-3 flex items-center justify-between space-x-2">
                <div className="shrink-0 basis-1/4 text-center font-semibold">
                  <h4 className="text-secondary text-xl">{bmi}</h4>
                  <h5 className="opacity-70 text-xxs">{constants.BMI.unit}</h5>
                </div>
                <Range
                  className="grow"
                  ranges={constants.BMI.ranges}
                  selectedIndex={selectedBMIRangeIndex}
                />
              </div>
            </ListItemContainer>
          );
        })()}
        {(() => {
          const hr = result?.vitals.heart_rate;
          const hrRange =
            constants["Heart Rate"].ranges[result?.posture ?? "resting"];
          const selectedHRRangeIndex = hrRange.reduce(
            (p, c, ci) => (hr >= c.point ? ci : p),
            0
          );
          return (
            <ListItemContainer
              label="Heart Rate"
              title={`Your Heart Rate is ${hrRange[selectedHRRangeIndex].label}`}
              desc={
                constants["Heart Rate"].info +
                "\n\n" +
                hrRange[selectedHRRangeIndex].desc
              }
            >
              <div className="py-3 flex items-center justify-between space-x-2">
                <div className="shrink-0 basis-1/4 text-center font-semibold">
                  <h4 className="text-secondary text-xl">{hr}</h4>
                  <h5 className="opacity-70 text-xxs">
                    {constants["Heart Rate"].unit}
                  </h5>
                </div>
                <Range
                  className="grow"
                  ranges={hrRange}
                  selectedIndex={selectedHRRangeIndex}
                />
              </div>
            </ListItemContainer>
          );
        })()}
        <div className="grid grid-cols-2 gap-3">
          {[
            {
              label: "Blood Volume",
              value: result?.metadata.physiological_scores.bloodvolume,
            },
            {
              label: "Total Body Water",
              value: result?.metadata.physiological_scores.tbw,
            },
            ...(result?.metadata.physiological_scores.cal_carb !== "--" &&
            result?.metadata.physiological_scores.cal_fat !== "--"
              ? [
                  {
                    label: "Calorie from Carb",
                    value:
                      !(result?.metadata.physiological_scores.cal_carb >= 0) ||
                      !(result?.metadata.physiological_scores.cal_carb <= 100)
                        ? "--"
                        : result.metadata.physiological_scores.cal_carb,
                  },
                  {
                    label: "Calorie from Fat",
                    value:
                      !(result?.metadata.physiological_scores.cal_fat >= 0) ||
                      !(result?.metadata.physiological_scores.cal_fat <= 100)
                        ? "--"
                        : result.metadata.physiological_scores.cal_fat,
                  },
                ]
              : []),
          ].map((item, index) => (
            <button
              type="button"
              key={`section-smallitem-${index}`}
              className="px-3 py-4 rounded-2xl bg-primary text-white flex flex-col items-stretch justify-between text-left"
              onClick={() =>
                setModalState({
                  visible: true,
                  title: item.label,
                  desc: constants[item.label].description,
                })
              }
            >
              <div className="flex items-start justify-between space-x-1">
                <h2 className="text-xs font-medium">{item.label}</h2>
                <Info className="my-0.5 h-3 w-3 opacity-70" />
              </div>
              <div className="mt-3 break-all font-semibold">
                <h4 className="inline text-xl">{item.value}</h4>
                &nbsp;
                <h5 className="inline opacity-70 text-xxs">
                  {constants[item.label].unit}
                </h5>
              </div>
            </button>
          ))}
        </div>
        <div className="!mt-3 grid grid-cols-3 gap-3">
          {[
            {
              label: "Body Water %",
              value: result?.metadata.physiological_scores.tbwp,
            },
            {
              label: "Total Body Fat",
              value: result?.metadata.physiological_scores.bodyfat,
            },
            {
              label: "Body Weight",
              value: result?.metadata.physiological_scores.weight,
            },
          ].map((item, index) => (
            <button
              type="button"
              key={`section-smallitem-${index}`}
              className="px-3 py-4 rounded-2xl bg-[#0E84F1] text-white flex flex-col items-stretch justify-between text-left"
              onClick={() =>
                setModalState({
                  visible: true,
                  title: item.label,
                  desc: constants[item.label].description,
                })
              }
            >
              <div className="flex items-start justify-between space-x-1">
                <h2 className="text-xs font-medium">{item.label}</h2>
                <Info className="my-0.5 h-3 w-3 opacity-70" />
              </div>
              <div className="mt-3 break-all font-semibold">
                <h4 className="inline text-xl">{item.value}</h4>
                &nbsp;
                <h5 className="inline opacity-70 text-xxs">
                  {constants[item.label].unit}
                </h5>
              </div>
            </button>
          ))}
        </div>
        <PDFDownloadLink
          className="flex items-center justify-between space-x-4 py-3 px-6 rounded-xl bg-[#2B2B2B] text-white text-sm font-medium"
          document={reportPDF}
          fileName={`CarePlix-ScanResult-Report-${new Date(
            result?.entry_time?.replace?.(" ", "T") ?? 0
          ).toISOString()}.pdf`}
        >
          {({ loading }) => (
            <>
              <span>
                {loading ? "Loading document..." : "Download PDF Report"}
              </span>
              <Download className="flex-shrink-0 h-6 w-6" />
            </>
          )}
        </PDFDownloadLink>
        <div className="p-6 rounded-2xl bg-primary text-white">
          <h4 className="text-xs font-medium">Disclaimer :</h4>
          <p className="mt-2 text-xxs text-justify">
            For Investigational Use Only. CarePlix is not a substitute for the
            clinical judgment of a healthcare professional. CarePlix is intended
            to improve your awareness of general wellness. CarePlix does not
            diagnose, treat, mitigate or prevent any disease, symptom, disorder
            or abnormal physical state. Consult with a healthcare professional
            or emergency services if you believe you may have a medical issue.
          </p>
          <Link
            className="block mt-4 mx-auto max-w-max px-4 py-2 rounded-full bg-white text-primary text-xs font-medium"
            to="/"
            replace
          >
            Dashboard
          </Link>
        </div>
      </div>
      <Transition show={modalState.visible} as={Fragment}>
        <Dialog as={Fragment} onClose={closeModal}>
          <Transition.Child
            className="fixed top-0 bottom-0 left-0 right-0 bg-black/50 flex items-center justify-center"
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="duration-300 delay-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Transition.Child
              as={Fragment}
              enter="delay-150 duration-200"
              enterFrom="opacity-0 scale-50"
              enterTo="opacity-100 scale-100"
              leave="duration-100"
              leaveFrom="opacity-50 scale-50"
              leaveTo="opacity-0 scale-0"
            >
              <Dialog.Panel className="w-4/5 bg-white rounded-2xl p-6 flex flex-col items-center text-center">
                <Dialog.Title className="text-primary text-xs font-medium">
                  {modalState.title}
                </Dialog.Title>
                <Dialog.Description className="mt-2 text-primary/70 text-xxs">
                  {modalState.desc}
                </Dialog.Description>
                <button
                  className="mt-4 px-4 py-2 rounded-full bg-primary text-white text-xs font-medium"
                  type="button"
                  onClick={closeModal}
                >
                  Close
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </Transition.Child>
        </Dialog>
      </Transition>
    </section>
  ) : (
    <Navigate to="/" replace />
  );
};

export default ScanResult;
